import React from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Building2,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Eye,
  ExternalLink,
  Lock,
  MapPin,
  MessageCircle,
  MousePointerClick,
  Phone,
  Search,
  Shield,
  Sparkles,
  TrendingUp,
  User,
  UserCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { SEO_IMAGES } from '../utils/seo';

export default function ComoFuncionaPJPage() {
  const valueCards = [
    {
      icon: Eye,
      title: 'Mais presença nas buscas',
      description: 'Seu perfil fica estruturado para aparecer quando pacientes procuram por especialidade, cidade, convênio ou necessidade.',
    },
    {
      icon: ClipboardCheck,
      title: 'Dados profissionais organizados',
      description: 'CRM, RQE, endereço, convênios, telefone, fotos e informações de atendimento em uma página pública mais completa.',
    },
    {
      icon: BarChart3,
      title: 'Leitura de demanda',
      description: 'No Premium, você acompanha sinais de interesse, origem dos acessos e cliques de contato para decidir melhor.',
    },
  ];

  const steps = [
    {
      number: '01',
      icon: Search,
      title: 'Encontre ou cadastre o perfil',
      description: 'Procure se sua clínica ou perfil profissional já aparece no BuscaDoutor. Se não aparecer, faça um novo cadastro.',
    },
    {
      number: '02',
      icon: UserCheck,
      title: 'Valide e complete as informações',
      description: 'Confirme sua identidade, revise dados públicos e acrescente especialidades, convênios, endereços e canais permitidos.',
    },
    {
      number: '03',
      icon: TrendingUp,
      title: 'Acompanhe e evolua',
      description: 'Mantenha o perfil atualizado e use os recursos premium quando quiser mais visibilidade, métricas e conversão.',
    },
  ];

  const features = [
    'Perfil público otimizado para pacientes',
    'Busca por especialidade, cidade e convênio',
    'Fluxo de reivindicação para perfis já existentes',
    'Área do profissional para atualizar dados',
    'Métricas e origem dos acessos no Premium',
    'WhatsApp e recursos avançados apenas para Premium',
  ];

  return (
    <div className="min-h-screen bg-[#F7FBF9]" data-testid="como-funciona-pj-page">
      <SEOHead
        title="Para Clínicas e Profissionais de Saúde | BuscaDoutor"
        description="Cadastre, reivindique ou atualize seu perfil no BuscaDoutor para ampliar visibilidade, organizar dados de atendimento, convênios e métricas."
        canonicalUrl="/como-funciona-pj"
        imageUrl={SEO_IMAGES.business}
      />
      <Navbar />

      <section className="relative overflow-hidden border-b border-[#DDE8E4] bg-[#F5FAF8]">
        <div className="container-app grid gap-10 py-10 md:py-16 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)] lg:items-center">
          <div className="max-w-2xl">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/10 bg-white px-4 py-2 text-sm font-semibold text-primary shadow-sm">
              <Building2 className="h-4 w-4" />
              Para clínicas, médicos, dentistas e profissionais de saúde
            </div>

            <h1 className="text-3xl font-light leading-tight text-[#1A2421] sm:text-4xl md:text-5xl" style={{ fontFamily: 'Outfit,sans-serif' }}>
              Faça seu perfil trabalhar melhor pela sua presença digital.
            </h1>

            <p className="mt-5 max-w-xl text-base leading-relaxed text-[#59706A] md:text-lg">
              O BuscaDoutor ajuda pacientes a encontrarem profissionais com base em especialidade, localização e convênio. Para você, isso significa dados organizados, perfil mais confiável e um caminho claro para transformar buscas em contatos qualificados.
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link to="/claim" data-testid="pj-hero-claim-link">
                <Button size="lg" className="w-full rounded-full bg-primary px-7 text-white shadow-[0_10px_28px_rgba(28,78,66,0.18)] hover:bg-[#2D6D5D] sm:w-auto">
                  Reivindicar perfil
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/cadastro?type=provider" data-testid="pj-hero-register-link">
                <Button size="lg" variant="outline" className="w-full rounded-full border-primary/30 bg-white px-7 text-primary hover:bg-primary/5 sm:w-auto">
                  Cadastrar novo perfil
                </Button>
              </Link>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-[#59706A]">
              Recursos como WhatsApp em destaque, dados avançados e maior capacidade de conversão fazem parte da experiência Premium.
            </p>
          </div>

          <div className="relative">
            <div className="rounded-[28px] border border-white bg-white p-4 shadow-[0_18px_55px_rgba(28,78,66,0.12)]">
              <div className="rounded-xl border border-amber-200 bg-white p-5 shadow-sm ring-1 ring-amber-100">
                <div className="mb-4 rounded-2xl bg-[#1C4E42] p-3 text-white shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#F0C86A]">Perfil em destaque</p>
                      <p className="mt-0.5 text-xs text-white/65">Mais informações para ajudar na escolha do paciente</p>
                    </div>
                    <span className="rounded-full bg-white/12 px-3 py-1 text-xs font-semibold text-white">Premium</span>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-100 sm:h-20 sm:w-20">
                    <User className="h-8 w-8 text-slate-400" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700">
                        Destaque
                      </span>
                      <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
                        <Shield className="mr-1 h-3 w-3" />
                        Aceita convênio
                      </span>
                      <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        Perfil completo
                      </span>
                    </div>

                    <h2 className="text-base font-semibold leading-snug text-slate-900 md:text-lg">
                      Dra. Marina Andrade
                    </h2>
                    <p className="text-sm text-slate-600">Cardiologista</p>
                    <p className="mt-1 text-sm text-slate-500">CRM 12345/BA</p>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1">
                  {['Unimed', 'Bradesco Saúde', 'SulAmérica', '+3'].map((plan) => (
                    <span key={plan} className="rounded-full border border-emerald-100 bg-emerald-50/60 px-2 py-0.5 text-xs text-emerald-700">
                      {plan}
                    </span>
                  ))}
                </div>

                <div className="mt-3 rounded-xl border border-emerald-100 bg-[#F5FAF8] px-3 py-2">
                  <p className="mb-1.5 text-xs font-semibold text-emerald-800">Por que apareceu para você</p>
                  <div className="flex flex-wrap gap-1.5">
                    {['Especialidade compatível', 'Atende em Salvador - BA', 'Convênios informados'].map((reason) => (
                      <span key={reason} className="rounded-full border border-emerald-100 bg-white px-2 py-0.5 text-[11px] text-emerald-700">
                        {reason}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-3 flex items-start gap-2 border-t border-slate-100 pt-3">
                  <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
                  <div className="min-w-0">
                    <span className="block truncate text-sm text-slate-600">Clínica Vida Integrada</span>
                    <span className="text-xs text-slate-500">Salvador - BA</span>
                  </div>
                </div>

                <div className="mt-1 flex items-center gap-2">
                  <Phone className="h-4 w-4 flex-shrink-0 text-slate-400" />
                  <span className="text-sm font-medium text-primary">(71) 3000-0000</span>
                </div>

                <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                  <span className="text-sm text-slate-500">Prévia do perfil público</span>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-1.5 text-xs text-slate-500">
                      <ExternalLink className="h-3.5 w-3.5" />
                      Mapa
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-green-100 bg-green-50 px-2.5 py-1.5 text-xs text-green-700">
                      WhatsApp Premium
                    </span>
                    <span className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-white">
                      Ver perfil
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-10 md:py-14">
        <div className="container-app grid gap-4 md:grid-cols-3">
          {valueCards.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.title} className="rounded-2xl border border-[#DDE8E4] bg-white p-6 shadow-sm">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <h2 className="text-lg font-semibold text-[#1A2421]">{item.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-[#59706A]">{item.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-[#F7FBF9] py-12 md:py-16" data-testid="pj-premium-comparison">
        <div className="container-app">
          <div className="mx-auto mb-9 max-w-3xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">O que muda no Premium</span>
            <h2 className="mt-3 text-2xl font-semibold leading-tight text-[#1A2421] md:text-4xl" style={{ fontFamily: 'Outfit,sans-serif' }}>
              Do perfil encontrado ao contato mensurável.
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-[#59706A] md:text-base">
              O plano gratuito ajuda você a organizar e validar sua presença. O Premium transforma essa presença em canal de contato e leitura de demanda.
            </p>
          </div>

          <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr] lg:items-stretch">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              <div className="rounded-3xl border border-[#DDE8E4] bg-white p-6 shadow-sm">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                  <UserCheck className="h-5 w-5" />
                </div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Gratuito</p>
                <h3 className="mt-2 text-xl font-semibold text-[#1A2421]">Perfil validado e editável</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#59706A]">
                  Ideal para confirmar identidade, corrigir dados públicos, informar especialidade, endereço e deixar o perfil mais confiável.
                </p>
                <div className="mt-5 space-y-3">
                  {['Reivindicação segura por WhatsApp', 'Edição de dados profissionais', 'Página pública indexável'].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-sm text-[#315A50]">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      {item}
                    </div>
                  ))}
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Lock className="h-4 w-4" />
                    WhatsApp em destaque bloqueado
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-amber-200 bg-white p-6 shadow-[0_16px_42px_rgba(245,166,35,0.12)] ring-1 ring-amber-100">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                  <Sparkles className="h-5 w-5" />
                </div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700">Premium</p>
                <h3 className="mt-2 text-xl font-semibold text-[#1A2421]">Contato direto e métricas</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#59706A]">
                  Libera o WhatsApp no perfil e mostra quantas pessoas acessaram, clicaram e de onde vieram os interessados.
                </p>
                <div className="mt-5 space-y-3">
                  {[
                    'Botão de WhatsApp visível no perfil',
                    'Cliques em telefone e WhatsApp',
                    'Origem dos acessos por cidade e UF',
                    'Dashboard para acompanhar resultado',
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-sm font-medium text-[#315A50]">
                      <CheckCircle2 className="h-4 w-4 text-amber-600" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-[30px] border border-white bg-white p-4 shadow-[0_18px_55px_rgba(28,78,66,0.12)]">
              <div className="rounded-[24px] bg-[#F5FAF8] p-5 md:p-6">
                <div className="flex flex-col gap-4 border-b border-[#DDE8E4] pb-5 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">Painel Premium</p>
                    <h3 className="mt-1 text-xl font-semibold text-[#1A2421]">Veja o que seu perfil está gerando</h3>
                  </div>
                  <span className="inline-flex w-fit items-center rounded-full bg-[#1C4E42] px-3 py-1.5 text-xs font-semibold text-white">
                    Últimos 30 dias
                  </span>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  {[
                    { icon: Eye, value: '83', label: 'visitas no perfil' },
                    { icon: MessageCircle, value: '21', label: 'cliques no WhatsApp' },
                    { icon: Phone, value: '12', label: 'cliques no telefone' },
                  ].map((metric) => {
                    const Icon = metric.icon;
                    return (
                      <div key={metric.label} className="rounded-2xl border border-[#DDE8E4] bg-white p-4 shadow-sm">
                        <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                          <Icon className="h-4 w-4" />
                        </div>
                        <p className="text-3xl font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit,sans-serif' }}>{metric.value}</p>
                        <p className="mt-1 text-xs leading-snug text-[#59706A]">{metric.label}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_0.85fr]">
                  <div className="rounded-2xl border border-[#DDE8E4] bg-white p-4 shadow-sm">
                    <div className="mb-4 flex items-center justify-between">
                      <p className="text-sm font-semibold text-[#1A2421]">Origem dos acessos</p>
                      <MapPin className="h-4 w-4 text-primary" />
                    </div>
                    {[
                      ['Salvador, BA', '42 acessos', 'w-[82%]'],
                      ['Lauro de Freitas, BA', '18 acessos', 'w-[45%]'],
                      ['Camaçari, BA', '9 acessos', 'w-[26%]'],
                    ].map(([city, count, width]) => (
                      <div key={city} className="mb-3 last:mb-0">
                        <div className="mb-1 flex items-center justify-between gap-3 text-xs">
                          <span className="font-medium text-[#315A50]">{city}</span>
                          <span className="text-[#59706A]">{count}</span>
                        </div>
                        <div className="h-2 rounded-full bg-[#E4EEE9]">
                          <div className={`h-2 rounded-full bg-primary ${width}`} />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-amber-700 shadow-sm">
                      <MousePointerClick className="h-5 w-5" />
                    </div>
                    <p className="text-sm font-semibold text-[#1A2421]">O dado vira decisão</p>
                    <p className="mt-2 text-sm leading-relaxed text-[#6B5B2B]">
                      Se o perfil recebe visitas, mas poucos cliques, o profissional sabe que precisa melhorar foto, descrição, convênios ou canais de contato.
                    </p>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-green-100 bg-green-50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-green-800">WhatsApp liberado apenas no Premium</p>
                      <p className="mt-1 text-xs text-green-700">O paciente consegue iniciar contato direto quando o perfil está preparado para converter.</p>
                    </div>
                    <span className="inline-flex w-fit items-center gap-2 rounded-full bg-green-600 px-4 py-2 text-xs font-bold text-white">
                      <MessageCircle className="h-4 w-4" />
                      Chamar no WhatsApp
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container-app">
          <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Como funciona</span>
              <h2 className="mt-3 text-2xl font-semibold leading-tight text-[#1A2421] md:text-4xl" style={{ fontFamily: 'Outfit,sans-serif' }}>
                Um fluxo simples para sair de “cadastro incompleto” para perfil confiável.
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-[#59706A] md:text-base">
                A página foi pensada para reduzir dúvidas: quem já existe na base reivindica o perfil; quem ainda não aparece cria um cadastro novo.
              </p>
            </div>

            <div className="grid gap-4">
              {steps.map((step) => {
                const Icon = step.icon;
                return (
                  <div key={step.number} className="flex gap-4 rounded-2xl border border-[#DDE8E4] bg-white p-5 shadow-sm" data-testid={`pj-step-${step.number}`}>
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[#1C4E42] text-white">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">{step.number}</p>
                      <h3 className="mt-1 text-lg font-semibold text-[#1A2421]">{step.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-[#59706A]">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F0F7F4] py-12 md:py-16">
        <div className="container-app">
          <div className="grid gap-8 rounded-[28px] bg-white p-6 shadow-[0_16px_45px_rgba(28,78,66,0.08)] md:p-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                Perfil melhor, menos atrito
              </div>
              <h2 className="text-2xl font-semibold leading-tight text-[#1A2421] md:text-3xl" style={{ fontFamily: 'Outfit,sans-serif' }}>
                O paciente decide mais rápido quando encontra informação útil.
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-[#59706A] md:text-base">
                Para clínicas e profissionais, a melhoria mais importante não é apenas “aparecer”. É aparecer com dados suficientes para gerar confiança.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {features.map((feature) => (
                <div key={feature} className="flex items-start gap-3 rounded-2xl border border-[#DDE8E4] bg-[#F7FBF9] p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                  <p className="text-sm font-medium leading-relaxed text-[#315A50]">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1C4E42] py-14 md:py-18" data-testid="pj-cta-final">
        <div className="container-app">
          <div className="mx-auto max-w-3xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F0C86A]">Pronto para organizar sua presença?</span>
            <h2 className="mt-3 text-3xl font-light leading-tight text-white md:text-4xl" style={{ fontFamily: 'Outfit,sans-serif' }}>
              Reivindique seu perfil ou cadastre sua clínica no BuscaDoutor.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/65 md:text-base">
              Comece pela validação do perfil existente. Se sua clínica ainda não estiver na base, crie um novo cadastro.
            </p>
            <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/claim" data-testid="pj-cta-claim-link">
                <Button size="lg" className="w-full rounded-full bg-white px-7 text-primary hover:bg-white/90 sm:w-auto">
                  Reivindicar perfil
                </Button>
              </Link>
              <Link to="/cadastro?type=provider" data-testid="pj-cta-register-link">
                <Button size="lg" variant="outline" className="w-full rounded-full border-white/30 px-7 text-white hover:bg-white/10 sm:w-auto">
                  Cadastrar novo perfil
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
